import React, { useState } from 'react'
import { localizer } from '../../../Translator'
import { simulatedExamples } from './simulatedExamples'

const SIMULATION_LABELS = {
  en: {
    notice: 'Local simulation only. No request is sent. These demo tokens cannot authenticate API calls.',
    priceNotice: 'Local simulation only. No request is sent. Availability and prices are illustrative, not a real quote. Amounts are in euro cents; VAT is a percentage.',
    request: 'Simulated request',
    queryValue: 'Query parameter returnids (JSON before URL encoding, not a request body):',
    operationNotice: 'Local simulation only. No request is sent, no data is changed and no file is uploaded or downloaded. All data is illustrative.',
    response: 'Simulated response',
    rotation: 'Refreshing replaces both tokens with a new pair.'
  },
  es: {
    notice: 'Simulación local. No se envía ninguna petición. Estos tokens de ejemplo no sirven para autenticar llamadas a la API.',
    priceNotice: 'Simulación local. No se envía ninguna petición. La disponibilidad y los precios son ilustrativos, no una cotización real. Los importes están en céntimos de euro; el IVA es un porcentaje.',
    request: 'Petición simulada',
    queryValue: 'Parámetro de consulta returnids (JSON antes de codificarlo en la URL, no es el cuerpo de la petición):',
    operationNotice: 'Simulación local. No se envía ninguna petición, no se modifican datos ni se suben o descargan archivos. Todos los datos son ilustrativos.',
    response: 'Respuesta simulada',
    rotation: 'El refresco sustituye ambos tokens por un nuevo par.'
  },
  pt: {
    notice: 'Simulação local. Não é enviado nenhum pedido. Estes tokens de exemplo não permitem autenticar chamadas à API.',
    priceNotice: 'Simulação local. Não é enviado nenhum pedido. A disponibilidade e os preços são ilustrativos, não uma cotação real. Os montantes estão em cêntimos de euro; o IVA é uma percentagem.',
    request: 'Pedido simulado',
    queryValue: 'Parâmetro de consulta returnids (JSON antes da codificação na URL, não é o corpo do pedido):',
    operationNotice: 'Simulação local. Não é enviado nenhum pedido, não são alterados dados nem enviados ou descarregados ficheiros. Todos os dados são ilustrativos.',
    response: 'Resposta simulada',
    rotation: 'A atualização substitui ambos os tokens por um novo par.'
  }
}

function ExampleSimulation({ mode }) {
  const [showResponse, setShowResponse] = useState(false)
  const labels = SIMULATION_LABELS[localizer.getLanguage()] || SIMULATION_LABELS.en
  const isRefresh = mode === 'refresh'
  const isPrice = mode === 'price'
  const example = simulatedExamples[mode]
  const notice = isPrice ? labels.priceNotice : mode === 'login' || isRefresh ? labels.notice : labels.operationNotice

  return (
    <div className="docs-auth-simulation">
      <p>{notice}</p>
      <button type="button" className="docs-try-button" onClick={() => setShowResponse(true)}>
        Try it
      </button>
      <div aria-live="polite">
        {showResponse && (
          <React.Fragment>
            <p>{labels.request}</p>
            <CodeBlock language="http" code={example.request} />
            {example.queryValue && (
              <div className="docs-simulated-query">
                <p>{labels.queryValue}</p>
                <CodeBlock language="json" code={example.queryValue} />
              </div>
            )}
            <div className="docs-simulated-response">
              <p>{labels.response}: <strong>200 OK</strong></p>
              <CodeBlock language={example.language} code={example.response} />
              {isRefresh && <p>{labels.rotation}</p>}
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

const LANGUAGE_ALIASES = {
  node: 'javascript',
  js: 'javascript',
  php: 'php',
  csharp: 'csharp',
  cs: 'csharp',
  curl: 'shell',
  bash: 'shell',
  sh: 'shell'
}

const KEYWORDS = {
  javascript: ['await', 'const', 'let', 'var', 'async', 'return', 'new', 'fetch', 'JSON', 'stringify'],
  python: ['import', 'from', 'as', 'await', 'def', 'return', 'with', 'json', 'requests'],
  php: ['array', 'curl_init', 'curl_setopt_array', 'curl_exec', 'curl_close', 'json_encode', 'json_decode', 'true', 'false', 'null'],
  csharp: ['using', 'var', 'new', 'await', 'return', 'async', 'public', 'private', 'class', 'HttpClient', 'HttpRequestMessage', 'JsonContent'],
  java: ['import', 'var', 'new', 'return', 'public', 'private', 'class', 'HttpRequest', 'HttpResponse', 'URI']
}

function classForToken(token, language, tokenEnd, code) {
  if (/^https?:\/\//.test(token)) return 'docs-token-url'
  if (/^\/\//.test(token) || /^#/.test(token)) return 'docs-token-comment'
  if (/^\$[A-Za-z_]\w*$/.test(token)) return 'docs-token-variable'
  if (/^--?[A-Za-z][\w-]*$/.test(token)) return 'docs-token-flag'
  if (/^(GET|POST|PUT|PATCH|DELETE)$/i.test(token)) return 'docs-token-method'
  if (/^\d+(\.\d+)?$/.test(token)) return 'docs-token-number'
  if (/^(true|false|null)$/i.test(token)) return 'docs-token-literal'

  const keywordSet = KEYWORDS[language] || []
  if (keywordSet.includes(token)) return 'docs-token-keyword'

  if (/^["'`]/.test(token)) {
    const next = code.slice(tokenEnd).match(/^\s*:/)
    return language === 'json' && next ? 'docs-token-property' : 'docs-token-string'
  }

  return null
}

function highlightCode(code, language, keyPrefix) {
  const normalizedLanguage = LANGUAGE_ALIASES[language] || language || 'text'
  const tokenRegex = /(https?:\/\/[^\s"')]+)|(\/\/.*|#.*)|("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`)|(\$[A-Za-z_]\w*)|(\b(?:GET|POST|PUT|PATCH|DELETE)\b)|(\b(?:true|false|null)\b)|(\b\d+(?:\.\d+)?\b)|(\b[A-Za-z_][\w.]*\b)|(\B--?[A-Za-z][\w-]*\b)/g
  const nodes = []
  let lastIndex = 0
  let match
  let index = 0

  while ((match = tokenRegex.exec(code)) !== null) {
    if (match.index > lastIndex) nodes.push(code.substring(lastIndex, match.index))

    const token = match[0]
    const className = classForToken(token, normalizedLanguage, tokenRegex.lastIndex, code)
    if (className) {
      nodes.push(<span key={keyPrefix + '-' + (index++)} className={className}>{token}</span>)
    } else {
      nodes.push(token)
    }

    lastIndex = tokenRegex.lastIndex
  }

  if (lastIndex < code.length) nodes.push(code.substring(lastIndex))
  return nodes
}

/**
 * Single fenced code block with a "Copy" button.
 */
export function CodeBlock({ language, code }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
    }
  }

  return (
    <div className="docs-code-block">
      <div className="docs-code-toolbar">
        <span className="docs-code-lang">{language || 'text'}</span>
        <button type="button" className="docs-copy-button" onClick={handleCopy}>{copied ? 'Copied!' : 'Copy'}</button>
      </div>
      <pre className="docs-pre"><code>{highlightCode(code, language, 'code')}</code></pre>
    </div>
  )
}

/**
 * Multi-language code sample, similar to the tabbed snippets on trackingmore.com/docs.
 * Source format (used inside a ```tabs fence in the markdown content):
 *
 * ===curl
 * curl -X GET ...
 * ===node
 * const res = await fetch(...)
 */
export function CodeTabs({ source, simulation }) {
  const parts = source.split(/^===\s*(\S+)\s*$/m).slice(1)
  const tabs = []
  for (let i = 0; i < parts.length; i += 2) {
    tabs.push({ language: parts[i], code: (parts[i + 1] || '').replace(/^\n/, '').replace(/\n$/, '') })
  }
  tabs.sort((left, right) => left.language.localeCompare(right.language))

  const [activeIndex, setActiveIndex] = useState(0)
  const [copied, setCopied] = useState(false)

  if (tabs.length === 0) return null
  const active = tabs[activeIndex]

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(active.code).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
    }
  }

  return (
    <div className="docs-code-block docs-code-tabs">
      <div className="docs-code-toolbar">
        <div className="docs-tab-list">
          {tabs.map((tab, index) => (
            <button
              type="button"
              key={tab.language}
              className={'docs-tab-button' + (index === activeIndex ? ' docs-tab-button-active' : '')}
              onClick={() => setActiveIndex(index)}
            >
              {tab.language}
            </button>
          ))}
        </div>
        <button type="button" className="docs-copy-button" onClick={handleCopy}>{copied ? 'Copied!' : 'Copy'}</button>
      </div>
      <pre className="docs-pre"><code>{highlightCode(active.code, active.language, 'tab-' + active.language)}</code></pre>
      {simulation && <ExampleSimulation mode={simulation} />}
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/docs/api/CodeTabs.js